import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "inset-0 fixed flex items-center justify-center"
}
const _hoisted_2 = {
  key: 1,
  class: "main-wrapper bg-slate-50 hide-scrolls"
}

import Loader from "@/Partials/Loader.vue";
import Navigation from "@/Components/Navigation.vue";
import SidebarNavigation from "@/Components/SidebarNavigation.vue";
import FetchFundraisersQuery from "@/Graphql/Stores/FetchFundraisers.query.graphql";
import { IFundRaiserData, IFundRaiserVariable } from "@/interfaces/StoreInterfaces";
import { useQuery } from "@vue/apollo-composable";
import { watch, onMounted, inject, ref, onBeforeMount } from "vue";
import { useMainStore } from "@/store/MainStore";
import { useAuthStore } from "@/store/AuthStore";
import { getCurrentUser } from "@/Services/AuthService";
import Alert from "@/Partials/Alert.vue";
import { ALERT_TYPE } from "@/Constants/Alert";
import ZendeskHelper from "@/zendesk-helper";
import { IEmitter } from "@/Services/emitter";

//Stores
const IDLE_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds


export default /*@__PURE__*/_defineComponent({
  __name: 'MainLayout',
  setup(__props) {

const mainStore = useMainStore();
const authStore = useAuthStore();
// Emitter
const emitter: IEmitter | undefined = inject("emitter");

const { result, error } = useQuery<IFundRaiserData, IFundRaiserVariable>(FetchFundraisersQuery, {
  input: {
    searchBy: "",
    searchValue: "",
    orderBy: "",
    orderBySort: "",
    demo: true,
    all: true,
    offset: 0,
    limit: 0,
  },
});

watch(error, () => {
  console.error("Failed to Fetch Campaigns GraphQL Error");
  mainStore.setCampaignIds([]);
});
watch(result, () => {
  if (result.value?.storeGLCampaigns.exceptionCode) {
    console.error(
      `Failed to Fetch Campaigns with Exception Code ${result.value?.storeGLCampaigns.exceptionCode} \n Error Message: ${result.value.storeGLCampaigns.message}`
    );
    mainStore.setCampaignIds([]);
  }
  const campaignIds = result?.value?.storeGLCampaigns?.campaigns?.map(({ id }) => id) ?? [];
  mainStore.setCampaignIds(campaignIds);
  fetchCurrentUser();
  mainStore.setCampaigns(result?.value?.storeGLCampaigns?.campaigns ?? []);
});
const fetchCurrentUser = async () => {
  const userData = await getCurrentUser();
  if (userData?.me) {
    authStore.setLoggedInUser(userData.me);
  }
};

const alertRef = ref<InstanceType<typeof Alert>>();
emitter?.on("displayAlert", (alert: { title: string; text: string; type: string }) => showAlert(alert));
const showAlert = (alert: any) => {
  alertRef.value?.displayAlert(ALERT_TYPE.ERROR, {
    title: `${alert.title}`,
    text: alert.text ?? "Something went wrong",
  });
};

// IDLE TIME MONITOR
let lastActivityTime = Date.now();
const checkIdleTime = () => {
  const currentTime = Date.now();
  const elapsedTime = currentTime - lastActivityTime;
  if (elapsedTime >= IDLE_TIME) {
    // User has been idle for more than 5 minutes, reload (hard refresh) the app
    window.location.reload();
  }
};

const updateUserActivity = () => {
  lastActivityTime = Date.now();
};

setInterval(checkIdleTime, 1000); // Check idle time every second

// Set up event listeners for user interactions
document.addEventListener("mouseover", updateUserActivity);
document.addEventListener("mousedown", updateUserActivity);
document.addEventListener("mousemove", updateUserActivity);
document.addEventListener("keydown", updateUserActivity);
document.addEventListener("click", updateUserActivity);
document.addEventListener("dblclick", updateUserActivity);
// If user switches to another tab
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") {
    checkIdleTime();
  }
});
const removeAllEventListeners = () => {
  document.removeEventListener("mousemove", updateUserActivity);
  document.removeEventListener("keypress", updateUserActivity);
  document.removeEventListener("mousemove", updateUserActivity);
  document.removeEventListener("keydown", updateUserActivity);
  document.removeEventListener("click", updateUserActivity);
  document.removeEventListener("dblclick", updateUserActivity);
};

onBeforeMount(() => {
  removeAllEventListeners();
});

onMounted(async () => {
  updateUserActivity(); // Start tracking user activity status
  await fetchCurrentUser();
  ZendeskHelper.renderWidget();
});

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(Alert, {
      ref_key: "alertRef",
      ref: alertRef
    }, null, 512),
    _createVNode(Navigation),
    _createVNode(SidebarNavigation),
    (_unref(mainStore).isLoading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(Loader)
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_component_router_view)
        ]))
  ]))
}
}

})